/**
 * numberColor — single source of truth for the app-wide number-coloring
 * convention (Sept 2026): loss/negative → red, profit/positive → green,
 * zero → dark grey. Every screen/component/chart/table/tooltip that shows
 * a directional financial number (PnL, % change, returns, deltas) should
 * go through this module instead of hand-rolling `value >= 0 ? ... : ...`.
 *
 * Colors resolve to the existing --success/--danger brand tokens
 * (index.css) plus the new --zero dark-grey token, via the
 * .text-success/.text-danger/.text-zero utility classes already defined
 * there — NOT to Tailwind's default green-500/red-500/emerald-500/etc,
 * which is what most call sites used before this pass (inconsistent hexes
 * site-wide).
 *
 * Explicitly OUT of scope (do not run through this module): absolute
 * prices, quantities/counts, IDs/dates, non-directional scores (risk
 * score, SLA %, quiz %, confidence %), and candlestick chart bar colors
 * (visual price direction, not a rendered number).
 */
import { fmtKES, fmtPct } from "@/lib/api";

/**
 * signTone — "pos" | "neg" | "zero" based on the DISPLAYED (rounded) value,
 * not the raw one. A value like -0.001 that rounds to "0.00" at the given
 * precision reads as zero on screen, so it's treated as zero here too.
 */
export function signTone(value, digits = 2) {
    const n = Number(value);
    if (value === null || value === undefined || Number.isNaN(n)) return "zero";
    const rounded = Number(n.toFixed(digits));
    if (rounded === 0) return "zero";
    return rounded > 0 ? "pos" : "neg";
}

/** Text-color utility class for a given tone. */
export function toneClassName(tone) {
    if (tone === "pos") return "text-success";
    if (tone === "neg") return "text-danger";
    return "text-zero";
}

/**
 * toneVar — raw CSS color value for contexts that can't take a class
 * (recharts stroke/fill props, lightweight-charts color options, inline
 * style objects, SVG attributes).
 */
export function toneVar(tone) {
    if (tone === "pos") return "hsl(var(--success))";
    if (tone === "neg") return "hsl(var(--danger))";
    return "hsl(var(--zero))";
}

/** One-shot helpers: raw value in, class/CSS-color out. */
export function toneClassFor(value, digits = 2) {
    return toneClassName(signTone(value, digits));
}
export function toneVarFor(value, digits = 2) {
    return toneVar(signTone(value, digits));
}

/**
 * signedPrefix — the "+" prefix so many call sites add by hand for
 * positive values. Never added for zero/negative — fmtKES/fmtPct already
 * render the "-" sign themselves for negatives.
 */
export function signedPrefix(value, digits = 2) {
    return signTone(value, digits) === "pos" ? "+" : "";
}

/**
 * fmtSignedKES / fmtSignedPct — format + tone in one call for the common
 * "colored KES/percent value with a leading +" pattern used across PnL
 * tables, KPI cards, and leaderboards.
 */
export function fmtSignedKES(value, digits = 2) {
    const tone = signTone(value, digits);
    return { text: `${signedPrefix(value, digits)}${fmtKES(value)}`, tone, className: toneClassName(tone) };
}
export function fmtSignedPct(value, digits = 2) {
    const tone = signTone(value, digits);
    return { text: `${signedPrefix(value, digits)}${fmtPct(value, digits)}`, tone, className: toneClassName(tone) };
}
